import { Header } from '@/components/header';
import { Footer } from '@/components/footer';
import CTA from '@/components/cta';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';

// Tech partner logos (already in tech_partner component but we might showcase a static grid too)
import awsOriginal from '@/assets/tech_partners/aws.svg';
import axisOriginal from '@/assets/tech_partners/axis.svg';
import hikOriginal from '@/assets/tech_partners/hikvision.svg';
import intelOriginal from '@/assets/tech_partners/intel.svg';
import nvidiaOriginal from '@/assets/tech_partners/nvidia.svg';
import dahuaOriginal from '@/assets/tech_partners/dahua.svg';

export default function TechnologyPartnersPage() {
  const { t } = useTranslation();
  // Partners with categories and descriptions (no i18n keys as requested)
  const partners = [
    {
      id: 'axis',
      name: 'Axis Communications',
      category: t('partners.cameras'),
      description:
        t('partners.axisDescription'),
      src: axisOriginal,
    },
    {
      id: 'hikvision',
      name: 'Hikvision',
      category: t('partners.cameras'),
      description:
        t('partners.hikDescription'),
      src: hikOriginal,
    },
    {
      id: 'dahua',
      name: 'Dahua',
      category: t('partners.cameras'),
      description:
        t('partners.dahuaDescription'),
      src: dahuaOriginal,
    },
    {
      id: 'intel',
      name: 'Intel',
      category: t('partners.compute'),
      description:
        t('partners.intelDescription'),
      src: intelOriginal,
    },
    {
      id: 'nvidia',
      name: 'NVIDIA',
      category: t('partners.compute'),
      description:  
        t('partners.nvidiaDescription'),
      src: nvidiaOriginal,
    },
    {
      id: 'aws',
      name: 'Amazon Web Services (AWS)',
      category: t('partners.cloud'),
      description:
        t('partners.awsDescription'),
      src: awsOriginal,
    },
  ] as const;

  const categories = [t('partners.all'), t('partners.cameras'), t('partners.compute'), t('partners.cloud')] as const;
  const [activeTab, setActiveTab] = useState<(typeof categories)[number]>('All');
  const filteredPartners = useMemo(
    () => (activeTab === 'All' ? partners : partners.filter((p) => p.category === activeTab)),
    [activeTab]
  );

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <Header />

      <section className="relative w-full overflow-hidden h-40 md:h-56 lg:h-[288px] bg-slate-900">
        {/* Background with Overlay */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 " />
          <div
            className="relative h-full w-full lg:w-[1920px] lg:h-[288px] mx-auto bg-black"
            style={{ aspectRatio: '1920 / 288' }}
          >
            <video
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
            >
              <source src="/partnersMobile.mp4" type="video/mp4" media="(max-width: 768px)" />
              <source src="/partners.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          
        </div>


      </section>

      {/* Hero */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 dark:text-white">
            {t('header.technology_partners')}
          </h1>
          <p className="mt-5 text-lg md:text-xl text-slate-600 dark:text-slate-300">
            {t('partners.heroSubtitle')}
          </p>
          <p className="mt-10 text-lg md:text-xl text-slate-600 dark:text-slate-300 text-justify">
            {t('partners.description')}
          </p>
        </div>
      </section>



      {/* Partners with tabs and descriptions */}
      <section className="py-16">
        <div className="container mx-auto px-4">

          {/* Tabs */}
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 mb-8">
            {categories.map((cat) => {
              const active = activeTab === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveTab(cat)}
                  className={
                    'px-4 py-2 rounded-full text-sm md:text-base transition border ' +
                    (active
                      ? 'bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300 dark:bg-slate-900 dark:text-slate-200 dark:border-slate-700 hover:dark:border-slate-600')
                  }
                >
                  {cat}
                </button>
              );
            })}
          </div>

          {/* Partner cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPartners.map((p) => (
              <div
                key={p.id}
                className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 hover:shadow-md transition"
              >
                <div className="flex items-center gap-3 mb-4">
                  <img src={p.src} alt={p.name} className="h-8 w-auto object-contain" loading="lazy" />
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{p.name}</h3>
                    <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                      {p.category}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + Footer */}
      <CTA />
      <Footer />
    </div>
  );
}
